import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';

// Member Browse (SHARC_Spec_Member_Browse_2026-10-07) — rebuilt on
// ArtistList.js: one search line (member name, artist name, or city),
// Member / City / State / Country / Artists / # of Artists / # of Shows
// columns, all sortable, mobile cards + sort row, and FestivalList.js /
// PromoterList.js's jump-to-page pagination. The Verified picklist is gone.
//
// Location is the member's CURRENT location (members.city / state_id /
// country_id), shown as stored codes — not hometown (Andy decision).

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['‘’‛`\-.,:!?&]/g, '')
        .toLowerCase();

const PAGE_SIZE = 25;

const NUMERIC_COLS = new Set(['total_artists', 'total_shows']);

// Shared between the desktop table headers and the mobile "Sort by" select,
// so both stay in sync automatically if columns ever change.
// artists_text is a derived key (the displayed artist list joined with ", "),
// so the Artists column sorts alphabetically by top artist, then the next.
const SORT_COLUMNS = [
    { col: 'name',          label: 'Member' },
    { col: 'city',          label: 'City' },
    { col: 'state_code',    label: 'State' },
    { col: 'country_code',  label: 'Country' },
    { col: 'artists_text',  label: 'Artists' },
    { col: 'total_artists', label: '# of Artists' },
    { col: 'total_shows',   label: '# of Shows' },
];

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

// performed_artists arrives most-shows-first from getMembers.js; guard
// against an older Lambda (field absent) by falling back to [].
const artistsOf = (m) => (Array.isArray(m.performed_artists) ? m.performed_artists : []);

// ── Comma-separated artist links (shared by table cell + mobile card) ──────
function ArtistLinks({ artists }) {
    if (artists.length === 0) return <span style={S.dash}>—</span>;
    return (
        <>
            {artists.map((a, i) => (
                <React.Fragment key={a.id}>
                    <Link to={`/artists/${a.slug || a.id}`} style={S.link}>{a.name}</Link>
                    {i < artists.length - 1 && ', '}
                </React.Fragment>
            ))}
        </>
    );
}

// ── Jump-to-page pagination (local copy of FestivalDetail.js's Pagination —
// copy-per-file convention, see context doc Section 12) ────────────────────
function JumpPagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={S.pageJumpWrap}>
            <span style={S.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={S.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={S.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Mobile card (replaces the table below 768px) ───────────────────────────
// A plain container rather than one big <Link> (unlike Artist/Promoter cards):
// it holds several artist links, and links can't be nested.
function MemberCardMobile({ member }) {
    const location = [member.city, member.state_code, member.country_code].filter(Boolean).join(', ');
    const artistCount = Number(member.total_artists) || 0;
    const showCount = Number(member.total_shows) || 0;
    return (
        <div style={mobileStyles.card}>
            <Link to={`/members/${member.slug || member.id}`} style={mobileStyles.cardName}>{member.name}</Link>
            {location && <div style={mobileStyles.cardLocation}>{location}</div>}
            {artistCount > 0 && (
                <div style={mobileStyles.cardArtists}>
                    <ArtistLinks artists={artistsOf(member)} />
                </div>
            )}
            <div style={mobileStyles.cardStatsRow}>
                <span>{plural(artistCount, 'artist')} · {plural(showCount, 'show')}</span>
            </div>
        </div>
    );
}

function MemberList() {
    const isMobile = useIsMobile();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Filter state — single search line only (spec §4.2)
    const [search, setSearch] = useState('');

    // Sort state — default Member A–Z (spec §2)
    const [sortCol, setSortCol] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage]       = useState(1);

    useEffect(() => {
        document.title = 'SHARC - Members';
        axios.get(`${config.API_URL}/members`)
            .then(res => {
                // Derive artists_text once, for the Artists column sort.
                const rows = (res.data.members || []).map(m => ({
                    ...m,
                    artists_text: artistsOf(m).map(a => a.name).join(', '),
                }));
                setMembers(rows);
                setLoading(false);
            })
            .catch(() => { setError('Failed to load members'); setLoading(false); });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    // Matches member name, current city, or any performed-with artist name.
    const filtered = useMemo(() => {
        if (!search) return members;
        const q = normalizeStr(search);
        return members.filter(m =>
            (m.name && normalizeStr(m.name).includes(q)) ||
            (m.city && normalizeStr(m.city).includes(q)) ||
            artistsOf(m).some(a => normalizeStr(a.name).includes(q))
        );
    }, [members, search]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const av = NUMERIC_COLS.has(sortCol) ? Number(a[sortCol] ?? 0) : (a[sortCol] ?? '');
            const bv = NUMERIC_COLS.has(sortCol) ? Number(b[sortCol] ?? 0) : (b[sortCol] ?? '');
            const cmp = NUMERIC_COLS.has(sortCol)
                ? av - bv
                : String(av).localeCompare(String(bv));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortCol, sortDir]);

    const clearFilters = () => {
        setSearch('');
        setPage(1);
    };

    const hasFilters = !!search;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    if (loading) return <div style={S.message}>Loading members...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            {/* ── Header ── */}
            <div style={isMobile ? { ...S.header, ...Smobile.header } : S.header}>
                <h1 style={S.title}>Members</h1>
                <div style={S.headerRight}>
                    <span style={S.count}>{sorted.length.toLocaleString()} of {members.length.toLocaleString()}</span>
                    <Link to="/add-member" style={S.addButton}>+ Add Member</Link>
                </div>
            </div>

            {/* ── Search (single line, no dropdowns) ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by member name, artist name or city..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    style={S.searchInput}
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Mobile-only sort control (table headers below handle this on desktop) ── */}
            {isMobile && (
                <div style={mobileStyles.sortRow}>
                    <select
                        value={sortCol}
                        onChange={e => { setSortCol(e.target.value); setPage(1); }}
                        style={mobileStyles.sortSelect}
                    >
                        {SORT_COLUMNS.map(({ col, label }) => (
                            <option key={col} value={col}>Sort: {label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        style={mobileStyles.sortDirBtn}
                        aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
                    </button>
                </div>
            )}

            {sorted.length === 0 ? (
                <div style={S.message}>
                    {hasFilters ? 'No members match your search.' : 'No members yet.'}
                </div>
            ) : isMobile ? (
                <div style={mobileStyles.cardList}>
                    {pageItems.map((m) => (
                        <MemberCardMobile key={m.id} member={m} />
                    ))}
                </div>
            ) : (
                <div style={S.tableContainer}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {SORT_COLUMNS.map(({ col, label }) => (
                                    <th
                                        key={col}
                                        style={{ ...S.th, ...(NUMERIC_COLS.has(col) ? S.thNum : {}), cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort(col)}
                                    >
                                        {label} <SortIcon col={col} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((m, idx) => (
                                <tr
                                    key={m.id}
                                    style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                >
                                    <td style={S.td}>
                                        <Link to={`/members/${m.slug || m.id}`} style={S.link}>{m.name}</Link>
                                    </td>
                                    <td style={S.td}>{m.city || '—'}</td>
                                    <td style={S.td}>{m.state_code || '—'}</td>
                                    <td style={S.td}>{m.country_code || '—'}</td>
                                    <td style={{ ...S.td, ...S.tdWrap }}>
                                        <ArtistLinks artists={artistsOf(m)} />
                                    </td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(m.total_artists) || 0}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(m.total_shows) || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <JumpPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
        </div>
    );
}

const S = {
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:       { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
    count:       { fontSize: '13px', color: '#666' },
    addButton:   { backgroundColor: '#bb86fc', color: '#121212', textDecoration: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px' },

    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    thNum:          { textAlign: 'right' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    // Artists is the one cell that wraps; the max width keeps a long list
    // from stretching the table instead of wrapping.
    tdWrap:         { whiteSpace: 'normal', minWidth: '200px', maxWidth: '360px', lineHeight: 1.5 },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    dash:           { color: '#e0e0e0' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    sortIcon:       { fontSize: '10px', marginLeft: '4px', color: '#555' },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // Jump-to-page pagination — copied from FestivalDetail.js / PromoterList.js
    pageJumpWrap:   { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel:  { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
};

// ── Mobile style overrides (same pattern as SHARC_Mobile_Responsive_Spec_v1.1) ──
const Smobile = {
    // "Members" heading + count/+Add group can overflow a nowrap flex row on
    // narrow viewports — allow it to wrap onto a second line instead.
    header: {
        flexWrap: 'wrap',
        gap: '10px',
    },
};

// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    sortRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    sortSelect: {
        flex: '1 1 auto',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 12px',
        fontSize: '13px',
        color: '#e0e0e0',
        boxSizing: 'border-box',
    },
    sortDirBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 14px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        color: '#e0e0e0',
    },
    cardName: {
        display: 'inline-block',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        textDecoration: 'none',
        marginBottom: '4px',
    },
    cardLocation: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardArtists: {
        fontSize: '13px',
        lineHeight: 1.5,
        marginBottom: '8px',
    },
    cardStatsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 12px',
        fontSize: '12px',
        color: '#aaa',
        paddingTop: '8px',
        borderTop: '1px solid #2a2a2a',
    },
};

export default MemberList;
